// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@awc/App/Components/Common/XngZone";
import { CustomAppState } from "@awc/App/Store/Reducers";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Change
    wrapper?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    video?: InjectableCss;
    wideColumnGridItem?: InjectableCss;
    actionsWrapper?: InjectableCss;
    rightColumnWrapper?: InjectableCss;
    wideColumnWrapper?: InjectableCss;
    titleContainer?: InjectableCss;
    title?: InjectableCss;
    partNumbersWrapper?: InjectableCss;
    overstockSale?: InjectableCss;
    documents?: InjectableCss;
    imageTablet?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;
const Article = getStyledWrapper("article");
const Section = getStyledWrapper("section");

const ProductDetailsView: React.FC<WidgetProps> = props => {
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon?.isTabletScreen);

    // XNG-Change: Restructure
    return (
        <>
            <Article {...styles.wrapper} data-test-selector="view">
                {!isTabletScreen && (
                    <Section aria-label={translate("Product Images")} {...styles.image}>
                        <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Image" />
                    </Section>
                )}
                <Section {...styles.description}>
                    <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Info" />
                </Section>
                <Section {...styles.video}>
                    <XngZone contentId={props.id} zoneName="Content2" hint="Video" />
                </Section>
                <Section {...styles.rightColumnWrapper}>
                    <StyledWrapper {...styles.titleContainer}>
                        <StyledWrapper {...styles.title}>
                            <XngZone contentId={props.id} zoneName="Content3" hint="Product Title" />
                            <StyledWrapper {...styles.partNumbersWrapper}>
                                <XngZone contentId={props.id} zoneName="Content4" hint="Part Numbers" />
                            </StyledWrapper>
                            {!isTabletScreen && (
                                <XngZone contentId={props.id} zoneName="Content10" hint="long description text" />
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.overstockSale}>
                            <XngZone contentId={props.id} zoneName="Content9" hint="over stock sale" />
                        </StyledWrapper>
                    </StyledWrapper>

                    {isTabletScreen && (
                        <Section {...styles.imageTablet}>
                            <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Image" />
                            <XngZone contentId={props.id} zoneName="Content10" hint="long description text" />
                        </Section>
                    )}
                    <XngZone contentId={props.id} zoneName="Content5" hint="Zone For Badges" />
                    <Section aria-label={translate("Product Purchase Options")} {...styles.actionsWrapper}>
                        <XngZone contentId={props.id} zoneName="Content6" hint="Attributes" />
                        <XngZone contentId={props.id} zoneName="Content7" hint="Actions" />
                    </Section>
                    <Section aria-label={translate("Documents")} {...styles.documents}>
                        <XngZone contentId={props.id} zoneName="Content11" hint="documents" />
                    </Section>
                </Section>
                <Section {...styles.wideColumnWrapper}>
                    <XngZone contentId={props.id} zoneName="Content8" hint="Wide Zone" />
                </Section>
            </Article>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
